import { useEffect, useState } from "react";
import Select from "react-select";
import { SeshTextChannelData } from "../../../../common/sesh_types";
import { DashboardUtils } from "../dashboard_utils";

interface ChannelDropdownData {
	value: string;
	label: string | JSX.Element;
	channel_data: SeshTextChannelData | undefined;
	options?: ChannelDropdownData[];
	isDisabled?: boolean;
}

const disabledStyle = {
	control: (base, state) => ({
		...base,
		background: "#BBBBBB",
		// Overwrittes the different states of border
		borderColor: "#BBBBBB",
		cursor: "not-allowed",
	}),
};

interface ChannelSelectState {
	value: ChannelDropdownData;
	options: ChannelDropdownData[];
	options_flat: ChannelDropdownData[];
}

function createCategoryDropdown(
	data: CategoryData,
	disabled: boolean
): ChannelDropdownData {
	return {
		value: data.category_id,
		label: data.category_name,
		options: data.children,
		channel_data: undefined,
	};
}

function createDropdownData(
	data: SeshTextChannelData,
	disabled: boolean,
	should_check_forum_tag_permissions?: boolean
): ChannelDropdownData {
	let className = "";
	let icon: JSX.Element;

	switch (data.type) {
		case "Text":
			icon = <i className="fas fa-hashtag"></i>;
			break;
		case "Forum":
			icon = <i className="fas fa-comments"></i>;
			break;
		case "Thread":
			className = "ml-2 mr-1";
			icon = (
				<i
					key="thread-icon"
					className="fas fa-level-up-alt fa-rotate-90 small-indent"
				></i>
			);
			break;
	}

	let is_disabled = disabled;

	let error_message: string;
	if (!data.has_write) {
		is_disabled = true;
		error_message = "Invalid Write Permissions";
	} else if (should_check_forum_tag_permissions) {
		if (
			data.type === "Forum" &&
			data.tag_settings.required &&
			data.tag_settings.tags.length === 0
		) {
			error_message = "Invalid Tag Permissions";
			is_disabled = true;
		}
	}

	return {
		value: data.id,
		label: (
			<div key={data.id} className="columns is-mobile is-vcentered">
				<div className="column">
					<p className={className}>
						<span
							key="spany span"
							style={{ color: "gray" }}
							className="mr-1"
						>
							{icon}
						</span>
						{data.name}
					</p>
				</div>
				{error_message && (
					<div>
						<div
							className={`column is-pulled-right dropdown-disabled-text`}
						>
							{error_message}
						</div>
						<div className="column is-narrow" />
					</div>
				)}
			</div>
		),
		channel_data: data,
		isDisabled: is_disabled,
	};
}

interface CategoryData {
	category_id: string;
	category_name: string;
	children: ChannelDropdownData[];
}

function hasPermsForChannel(channel_data: SeshTextChannelData) {
	if (!channel_data?.has_write) {
		return false;
	}

	if (channel_data.type === "Forum") {
		if (
			channel_data.tag_settings.required &&
			channel_data.tag_settings.tags.length === 0
		) {
			return false;
		}
	}

	return true;
}

export function ChannelSelectForm(props: {
	id?: string;
	name?: string | JSX.Element;
	initial_value?: SeshTextChannelData;
	channel_data: SeshTextChannelData[];
	info?: string | JSX.Element;
	updateFunc: (val: SeshTextChannelData) => void;
	disabled: boolean;
	is_event?: boolean;
	update_initially?: boolean;
	desired_channel_id?: string;
	sesh_channel_id?: string;
	include_threads: boolean;
	below_element?: JSX.Element;
	has_unsaved_changes?: boolean;
	should_check_forum_tag_permissions?: boolean;
}) {
	const channel_list = props.include_threads
		? props.channel_data
		: props.channel_data.filter((val) => val.type !== "Thread");

	let parent_to_threads_map = new Map<string, ChannelDropdownData[]>();

	const threads = channel_list.filter((val) => val.type === "Thread");
	const channel_list_no_threads = channel_list.filter(
		(val) => val.type !== "Thread"
	);

	for (const thread of threads) {
		let children_threads: ChannelDropdownData[];
		if (parent_to_threads_map.has(thread.category_id)) {
			children_threads = parent_to_threads_map.get(thread.category_id);
		} else {
			children_threads = [];
			parent_to_threads_map.set(thread.category_id, children_threads);
		}

		children_threads.push(
			createDropdownData(
				thread,
				props.disabled,
				props.should_check_forum_tag_permissions
			)
		);
	}

	let category_mapped_channels: CategoryData[] = [];
	for (let option of channel_list_no_threads) {
		if (
			props.sesh_channel_id &&
			option.id === props.sesh_channel_id &&
			props.is_event != null
		) {
			category_mapped_channels = [
				{
					category_id: "SESH_CHANNEL",
					category_name: `Sesh ${
						props.is_event ? "Event" : "Poll"
					} Channel`,
					children: [
						createDropdownData(
							option,
							props.disabled,
							props.should_check_forum_tag_permissions
						),
					].concat(parent_to_threads_map.get(option.id) ?? []),
				},
			].concat(category_mapped_channels);

			continue;
		}

		if (!option.category_id) {
			category_mapped_channels.push({
				category_id: null,
				category_name: null,
				children: [
					createDropdownData(
						option,
						props.disabled,
						props.should_check_forum_tag_permissions
					),
				].concat(parent_to_threads_map.get(option.id) ?? []),
			});

			continue;
		}

		let category_data = category_mapped_channels.find(
			(val) => val.category_id === option.category_id
		);
		if (!category_data) {
			category_data = {
				category_id: option.category_id,
				category_name: option.category_name,
				children: [],
			};

			category_mapped_channels.push(category_data);
		}

		category_data.children.push(
			createDropdownData(
				option,
				props.disabled,
				props.should_check_forum_tag_permissions
			)
		);

		category_data.children = category_data.children.concat(
			parent_to_threads_map.get(option.id) ?? []
		);
	}

	let options: ChannelDropdownData[] = [];
	for (let category_data of category_mapped_channels) {
		if (!category_data.category_id) {
			options = options.concat(category_data.children);
		} else {
			options.push(createCategoryDropdown(category_data, props.disabled));
		}
	}

	const options_flat: ChannelDropdownData[] = [];
	for (let root_option of options) {
		if (root_option.options && root_option.options.length > 0) {
			for (let option of root_option.options) options_flat.push(option);
		} else {
			options_flat.push(root_option);
		}
	}

	let initial_value_dropdown: ChannelDropdownData;

	if (props.initial_value) {
		initial_value_dropdown = options_flat.find(
			(val) => val.value === props.initial_value.id
		);
	}

	if (
		!initial_value_dropdown ||
		!hasPermsForChannel(initial_value_dropdown.channel_data)
	) {
		let first_channel: ChannelDropdownData;
		for (let option of options_flat) {
			if (!hasPermsForChannel(option.channel_data)) continue;
			first_channel = option;
			break;
		}

		if (first_channel) {
			initial_value_dropdown = first_channel;
		} else {
			DashboardUtils.ShowDefaultErrorAlert(
				"No valid channels found. Please ensure sesh and you have proper permissions in atleast one channel in the server."
			);
		}
	}

	const [state, setState] = useState({
		value: initial_value_dropdown,
		options: options,
		options_flat,
	});

	useEffect(() => {
		if (props.update_initially) {
			props.updateFunc(state.value.channel_data);
		}
	}, []);

	function renderHeader() {
		if (!props.name) {
			return null;
		}

		return DashboardUtils.CreateHeaderAndInfoButton(
			props.name,
			props.info,
			undefined,
			undefined,
			props.has_unsaved_changes,
			undefined,
			props.disabled
		);
	}

	function renderOverrideText() {
		if (
			props.desired_channel_id &&
			props.desired_channel_id !== state.value.channel_data.id
		) {
			const desired_channel_data = state.options_flat.find(
				(val) => val?.channel_data?.id == props.desired_channel_id
			);
			if (!desired_channel_data) {
				return;
			}

			return (
				<p className="mt-4 warning-note">
					Selected channel differs from server default: (#
					{desired_channel_data.channel_data.name})<br />
					<span
						className="link-chain-clickable-text"
						onClick={() => {
							handleChange(desired_channel_data);
						}}
					>
						Revert
					</span>
				</p>
			);
		}
		return null;
	}

	function renderChannelDropdown() {
		const style = props.disabled ? disabledStyle : undefined;

		return (
			<div className="field" id={props.id}>
				{renderHeader()}
				<div className="control">
					<Select
						//menuIsOpen={true}
						classNamePrefix="sesh-dropdown"
						filterOption={filterOptions}
						//styles={style}
						value={state.value}
						options={state.options}
						onChange={handleChange}
						isDisabled={props.disabled}
					/>
				</div>
				{renderOverrideText()}
				{props.below_element}
			</div>
		);
	}

	function handleChange(event: ChannelDropdownData) {
		setState({ ...state, value: event });
		props.updateFunc(event.channel_data);
	}

	return renderChannelDropdown();
}

function filterOptions(
	option: {
		label: string;
		value: string;
		data: { channel_data: SeshTextChannelData };
	},
	input: string
) {
	let lowercase_input = input.toLowerCase();
	let lowercase_channel = "#" + option.data.channel_data.name.toLowerCase();
	return lowercase_channel.includes(lowercase_input);
}
